import { mapCatalog } from '../../common/utils/util';
import { Suggestions } from '../search/interfaces/suggestion.interface';
import { generateSearchUrl } from '../search/utils/search.util';
import { SearchResultPage, SearchTermRedirects, SearchV3PopularSearchResult, SearchV3Result, SearchV3SupportResult } from './search-v3.interface';

export const getBusinessUnit = (label) => {
  const bu = {
    'prek-12': 'seg',
    content: 'content',
    highered: 'he',
    professional: 'professional',
  };

  return bu[label?.toLowerCase()];
};

export const getCatalogForRedirect = (businessUnit: string): string => {
  return businessUnit === 'products' ? 'ALL' : mapCatalog(businessUnit);
};

export function searchV3SuggestionMapper({
  searchResult,
  searchTermRedirects,
  selectedBusinessUnit,
  searchResultPage = SearchResultPage.DEFAULT,
  product = '',
  learningLevel = '',
  userType = '',
}: {
  searchResult: Suggestions;
  searchTermRedirects?: SearchTermRedirects;
  selectedBusinessUnit: string;
  searchResultPage?: SearchResultPage;
  product?: string;
  learningLevel?: string;
  userType?: string;
}): SearchV3Result[] {
  const searchV3result: SearchV3Result[] = [];

  searchResult.suggestions?.forEach((result) => {
    const redirectUrl = handleSearchRedirect({
      searchTermRedirects,
      term: result.terms,
      businessUnit: selectedBusinessUnit,
      searchResultPage,
      product,
      learningLevel,
      userType,
    });
    searchV3result.push({
      term: result.terms,
      count: result.count,
      redirectUrl,
    });
  });

  return searchV3result;
}

export function getSearchTermRedirect({
  searchTermRedirects,
  term,
  businessUnit,
}: {
  searchTermRedirects?: SearchTermRedirects;
  term: string;
  businessUnit: string;
}) {
  return searchTermRedirects?.redirects?.find((redirect) => {
    return (
      term.toLowerCase() === redirect.searchTerm.toLowerCase() &&
      [businessUnit, getCatalogForRedirect(businessUnit), 'ALL'].includes(redirect.catalog)
    );
  })?.target;
}

export function handleSearchRedirect({
  searchTermRedirects,
  term,
  businessUnit,
  discipline,
  searchResultPage = SearchResultPage.DEFAULT,
  product = '',
  learningLevel = '',
  userType = '',
}: {
  searchTermRedirects?: SearchTermRedirects;
  term: string;
  businessUnit: string;
  discipline?: string;
  searchResultPage?: SearchResultPage;
  product?: string;
  learningLevel?: string;
  userType?: string;
}) {
  // Check for custom redirects first (applies to both DEFAULT and SUPPORT)
  const customRedirect = getSearchTermRedirect({ searchTermRedirects, term, businessUnit });
  
  if (customRedirect) {
    return customRedirect;
  }
  
  // If no custom redirect, generate search URL with appropriate searchResultPage
  return generateSearchUrl({ 
    searchQuery: term, 
    businessUnit: businessUnit || undefined, 
    discipline,
    searchResultPage,
    product,
    learningLevel,
    userType,
  });
}

export function searchV3PopularSerachResultMapper(searchResult: SearchV3PopularSearchResult[]): SearchV3Result[] {
  /**
   * Regex will match below condition
   * it contain only numbers
   * it start with "mkt"
   */
  const regex = /^(?:\d+|mkt.*|)$/i;
  const searchV3result: SearchV3Result[] = [];

  searchResult?.forEach(({ term }) => {
    if (!regex.test(term)) {
      searchV3result.push({
        term,
        // here have used generateSearchUrl instead of handleSearchRedirect because we don't get business unit in popular search
        redirectUrl: generateSearchUrl({
          searchQuery: term,
        }),
      });
    }
  });

  return searchV3result;
}

export function searchV2SuggestionMapper({
  searchResult,
  searchResultPage = SearchResultPage.SUPPORT,
  businessUnit = 'support',
  isSearchPage = false,
  searchTermRedirects,
  product = '',
  learningLevel = '',
  userType = '',
}: {
  searchResult: string[];
  searchResultPage?: SearchResultPage;
  businessUnit?: string;
  isSearchPage?: boolean;
  searchTermRedirects?: SearchTermRedirects;
  product?: string;
  learningLevel?: string;
  userType?: string;
}): SearchV3SupportResult[] {
  const searchV3result: SearchV3SupportResult[] = [];

  // For each suggestion, check for custom redirects first, then generate URL
  searchResult?.forEach((term) => {
    const result: SearchV3SupportResult = { term };
    if (!isSearchPage) {
      result.redirectUrl = handleSearchRedirect({
        searchTermRedirects,
        term,
        businessUnit,
        searchResultPage,
        product,
        learningLevel,
        userType,
      });
    }
    searchV3result.push(result);
  });

  return searchV3result;
}